import API_BASE from '../config';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import LordIcon from '../components/LordIcon';
import LORDICONS from '../lordicons';
import CalendarBrandIcon from '../components/CalendarBrandIcon';
import AddressFields, { AddressFieldsValue } from '../components/AddressFields';
import AresSuggestInput, { AresSuggestion } from '../components/AresSuggestInput';
import FormRow, { fieldCls } from '../components/FormRow';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { applyUserLanguagePreference, setShiftsDefaultView } from '../utils/userPreferences';

const emptyAddress = (): AddressFieldsValue => ({
  street: '',
  country: 'Česká republika',
  region: '',
  city: '',
  zip: '',
});

function encodeAddress(a: AddressFieldsValue): string {
  if (!a.country && !a.region && !a.city && !a.street && !a.zip) return '';
  return JSON.stringify(a);
}

function addressFilled(a: AddressFieldsValue): boolean {
  return Boolean(a.street.trim() && a.city.trim() && a.zip.trim() && a.country.trim());
}

function formatAddressLabel(a: AddressFieldsValue): string {
  return [a.street, a.city, a.zip, a.region, a.country].filter(Boolean).join(', ');
}

const Register: React.FC = () => {
  const { t } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteFromUrl = (searchParams.get('invite') || '').trim().toUpperCase();
  const oauthToken = searchParams.get('oauth');

  const [role, setRole] = useState<'employer' | 'employee' | 'manager' | null>(inviteFromUrl ? 'employee' : null);
  const [step, setStep] = useState(inviteFromUrl ? 1 : 0);
  const [oauthProvider, setOauthProvider] = useState<'GOOGLE' | 'MICROSOFT' | 'APPLE' | null>(null);
  const [emailLocked, setEmailLocked] = useState(false);
  const [hasInviteCode, setHasInviteCode] = useState(Boolean(inviteFromUrl));
  const [inviteInput, setInviteInput] = useState(inviteFromUrl);
  const [inviteInfo, setInviteInfo] = useState<{ businessName: string; role: string } | null>(null);
  const [inviteLookupLoading, setInviteLookupLoading] = useState(false);
  const [inviteLookupError, setInviteLookupError] = useState<string | null>(null);
  const [inviteFromLink, setInviteFromLink] = useState(Boolean(inviteFromUrl));

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    sex: '',
    phone: '',
    // Employer - business
    businessName: '',
    companyName: '',
    ico: '',
    dic: '',
    businessEmail: '',
    businessPhone: '',
    website: '',
    description: '',
  });
  const [personalAddress, setPersonalAddress] = useState<AddressFieldsValue>(emptyAddress);
  const [businessAddress, setBusinessAddress] = useState<AddressFieldsValue>(emptyAddress);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const lookupInvite = async (code: string) => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setInviteInfo(null);
      setInviteLookupError(null);
      setInviteLookupLoading(false);
      return null;
    }
    setInviteLookupLoading(true);
    setInviteLookupError(null);
    try {
      const res = await fetch(`${API_BASE}/api/invite/info/${encodeURIComponent(trimmed)}`, {
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setInviteInfo(null);
        setInviteLookupError(data.error || t('register.invite.invalid'));
        return null;
      }
      const info = {
        businessName: data.businessName || '',
        role: data.role || 'EMPLOYEE',
      };
      setInviteInfo(info);
      setInviteLookupError(null);
      if (info.role === 'MANAGER') setRole('manager');
      else if (info.role === 'EMPLOYEE') setRole('employee');
      return info;
    } catch {
      setInviteInfo(null);
      setInviteLookupError(t('register.invite.invalid'));
      return null;
    } finally {
      setInviteLookupLoading(false);
    }
  };

  useEffect(() => {
    if (!oauthToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/oauth/pending/${encodeURIComponent(oauthToken)}`, {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(data.error || t('register.oauth.expired'));
          navigate('/login', { replace: true });
          return;
        }
        if (cancelled) return;
        setOauthProvider(
          data.provider === 'MICROSOFT'
            ? 'MICROSOFT'
            : data.provider === 'APPLE'
              ? 'APPLE'
              : 'GOOGLE'
        );
        setEmailLocked(true);
        setFormData((prev) => ({
          ...prev,
          email: data.email || prev.email,
          firstName: data.firstName || prev.firstName,
          lastName: data.lastName || prev.lastName,
        }));
        toast.info(t('register.oauth.linked'));
      } catch {
        toast.error(t('register.oauth.expired'));
        navigate('/login', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [oauthToken]);

  useEffect(() => {
    if (!hasInviteCode) {
      setInviteInfo(null);
      setInviteLookupError(null);
      setInviteLookupLoading(false);
      return;
    }
    const code = inviteInput.trim();
    if (!code) {
      setInviteInfo(null);
      setInviteLookupError(null);
      setInviteLookupLoading(false);
      return;
    }
    const delay = code === inviteFromUrl ? 0 : 400;
    const timer = window.setTimeout(() => {
      lookupInvite(code);
    }, delay);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteInput, hasInviteCode]);

  useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [avatarPreview, logoPreview]);

  const applyAresSuggestion = (s: AresSuggestion) => {
    setFormData((prev) => ({
      ...prev,
      companyName: s.companyName || prev.companyName,
      ico: s.ico || prev.ico,
      dic: s.dic || prev.dic,
      businessName:
        !prev.businessName.trim() || prev.businessName.trim() === prev.companyName.trim()
          ? s.companyName || prev.businessName
          : prev.businessName,
    }));
    if (s.address) {
      setBusinessAddress({
        street: s.address.street || '',
        country: s.address.country || 'Česká republika',
        region: s.address.region || '',
        city: s.address.city || '',
        zip: s.address.zip || '',
      });
    }
  };

  const onAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const onLogoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleNext = () => {
    setError(null);
    if (step === 0 && !role) {
      setError(t('register.please.select.role'));
      return;
    }
    if (step === 1) {
      if (
        !formData.firstName ||
        !formData.lastName ||
        !formData.username ||
        !formData.email ||
        !formData.password ||
        !formData.confirmPassword
      ) {
        setError(t('register.please.fill.all.fields'));
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError(t('register.passwords.dont.match'));
        return;
      }
    }
    if (step === 2) {
      if (!formData.sex || !formData.phone || !addressFilled(personalAddress)) {
        setError(t('register.please.fill.personal'));
        return;
      }
      if (role === 'employer') {
        if (!formData.businessName.trim()) {
          setError(t('register.please.enter.company'));
          return;
        }
        if (!formData.companyName.trim() || !formData.ico.trim()) {
          setError(t('register.please.fill.legal'));
          return;
        }
        if (!addressFilled(businessAddress)) {
          setError(t('register.please.fill.business.address'));
          return;
        }
      }
      if ((role === 'employee' || role === 'manager') && hasInviteCode) {
        const code = inviteInput.trim();
        if (!code) {
          setError(t('register.invite.required'));
          return;
        }
        if (inviteLookupLoading) {
          setError(t('register.invite.checking'));
          return;
        }
        if (inviteLookupError || !inviteInfo) {
          setError(inviteLookupError || t('register.invite.invalid'));
          return;
        }
      }
    }
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    setError(null);
    setStep((s) => s - 1);
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;
    const body = new FormData();
    body.append('avatar', avatarFile);
    const res = await fetch(`${API_BASE}/api/users/me/avatar`, {
      method: 'POST',
      credentials: 'include',
      body,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || t('profile.photo.failed'));
    return data.avatarUrl as string;
  };

  const uploadLogo = async (businessId: number) => {
    if (!logoFile || !businessId) return null;
    const body = new FormData();
    body.append('logo', logoFile);
    const res = await fetch(`${API_BASE}/api/businesses/${businessId}/logo`, {
      method: 'POST',
      credentials: 'include',
      body,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || t('settings.company.logo.failed'));
    return data.logo as string;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const apiRole = role === 'employer' ? 'OWNER' : role === 'employee' ? 'EMPLOYEE' : 'MANAGER';
      const res = await fetch(API_BASE + '/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          username: formData.username,
          password: formData.password,
          role: apiRole,
          sex: formData.sex,
          phone: formData.phone,
          address: encodeAddress(personalAddress),
          businessName: role === 'employer' ? formData.businessName : undefined,
          companyName: role === 'employer' ? formData.companyName : undefined,
          ico: role === 'employer' ? formData.ico : undefined,
          dic: role === 'employer' ? formData.dic || undefined : undefined,
          businessAddress: role === 'employer' ? encodeAddress(businessAddress) : undefined,
          businessEmail: role === 'employer' ? formData.businessEmail || undefined : undefined,
          businessPhone: role === 'employer' ? formData.businessPhone || undefined : undefined,
          website: role === 'employer' ? formData.website || undefined : undefined,
          description: role === 'employer' ? formData.description || undefined : undefined,
          oauthToken: oauthToken || undefined,
        }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('register.registration.failed'));
        toast.error(data.error || t('register.registration.failed'));
        return;
      }

      let user = data.user;
      try {
        const avatarUrl = await uploadAvatar();
        if (avatarUrl) user = { ...user, avatarUrl };
      } catch (uploadErr: any) {
        toast.warning(uploadErr?.message || t('profile.photo.failed'));
      }

      if (role === 'employer' && user?.businessId) {
        try {
          await uploadLogo(user.businessId);
        } catch (uploadErr: any) {
          toast.warning(uploadErr?.message || t('settings.company.logo.failed'));
        }
      }

      localStorage.setItem('user', JSON.stringify(user));
      if (user?.shiftsDefaultView === 'list' || user?.shiftsDefaultView === 'calendar') {
        setShiftsDefaultView(user.shiftsDefaultView, user.id);
      }
      applyUserLanguagePreference(user);
      window.dispatchEvent(new Event('userUpdated'));
      if (user?.calendarLinked) {
        toast.success(t('register.oauth.calendar.ready'));
      } else {
        toast.success(t('register.registration.success'));
      }
      const codeToJoin =
        (role === 'employee' || role === 'manager') && hasInviteCode
          ? inviteInput.trim().toUpperCase()
          : '';
      if (codeToJoin) {
        try {
          const joinRes = await fetch(API_BASE + '/api/join', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code: codeToJoin }),
          });
          const joinData = await joinRes.json().catch(() => ({}));
          if (!joinRes.ok) {
            toast.warning(joinData.error || t('register.invite.join.failed'));
          } else if (inviteInfo?.businessName) {
            toast.success(
              t('register.invite.joined').replace('{name}', inviteInfo.businessName)
            );
          }
        } catch {
          toast.warning(t('register.invite.join.failed'));
        }
      }
      navigate('/dashboard');
    } catch {
      setError(t('register.network.error'));
    } finally {
      setLoading(false);
    }
  };

  const panelCopy = [
    { title: t('register.panel.title.0'), desc: t('register.panel.desc.0') },
    { title: t('register.panel.title.1'), desc: t('register.panel.desc.1') },
    { title: t('register.panel.title.2'), desc: t('register.panel.desc.2') },
    { title: t('register.panel.title.3'), desc: t('register.panel.desc.3') },
  ][Math.min(step, 3)];

  const stepLabels = [
    t('register.choose.role'),
    t('register.basic.info'),
    role === 'employer'
      ? t('register.details.employer')
      : role === 'manager'
        ? t('register.details.manager')
        : t('register.details.employee'),
    t('register.review'),
  ];

  const Stepper = () => (
    <div className="flex items-center gap-2 mb-6">
      {stepLabels.map((label, idx) => {
        const active = step === idx;
        const done = step > idx;
        return (
          <React.Fragment key={`${idx}-${label}`}>
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${
                  active
                    ? 'bg-primary-600 text-white'
                    : done
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-400'
                }`}
              >
                {idx + 1}
              </div>
              {active && (
                <span className="text-sm font-medium text-gray-900 truncate">{label}</span>
              )}
            </div>
            {idx < stepLabels.length - 1 && (
              <div className={`flex-1 h-0.5 rounded min-w-[12px] ${done ? 'bg-primary-600' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const personalFields = (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
        {t('register.section.personal')}
      </p>
      <div className="flex items-center gap-4 py-2">
        <div className="relative flex-shrink-0">
          {avatarPreview ? (
            <img
              src={avatarPreview}
              alt=""
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
              {formData.firstName[0] || formData.lastName[0] ? (
                <span className="text-white font-bold text-lg">
                  {(formData.firstName[0] || '') + (formData.lastName[0] || '')}
                </span>
              ) : (
                <LordIcon src={LORDICONS.user} size={28} color="#FFFFFF" trigger="hover" />
              )}
            </div>
          )}
        </div>
        <div>
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg cursor-pointer transition-colors">
            <LordIcon src={LORDICONS.upload} trigger="hover" size={16} color="#6B7280" target="label" />
            {t('profile.upload.photo')}
            <input type="file" accept="image/*" className="hidden" onChange={onAvatarPick} />
          </label>
          <p className="text-xs text-gray-400 mt-1">{t('profile.photo.hint')}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
        <FormRow layout="stacked" icon={LORDICONS.users} label={t('register.sex')} required>
          <select
            id="sex"
            required
            value={formData.sex}
            onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
            className={fieldCls}
          >
            <option value="">{t('register.sex.select')}</option>
            <option value="male">{t('register.sex.male')}</option>
            <option value="female">{t('register.sex.female')}</option>
            <option value="other">{t('register.sex.other')}</option>
          </select>
        </FormRow>
        <FormRow layout="stacked" icon={LORDICONS.phone} label={t('register.phone')} required>
          <div className="register-phone w-full">
            <PhoneInput
              country="cz"
              value={formData.phone}
              onChange={(phone) => setFormData({ ...formData, phone })}
              inputProps={{
                name: 'phone',
                id: 'phone',
                required: true,
                autoFocus: false,
              }}
              containerClass="w-full"
              inputClass="!w-full !h-8 !pl-[48px] !pr-2.5 !text-[13px] !leading-none !rounded-[6px] !border !border-gray-200 !bg-white focus:!outline-none focus:!ring-1 focus:!ring-primary-500 focus:!border-primary-500"
              buttonClass="!h-8 !rounded-l-[6px] !border !border-gray-200 !border-r-0 !bg-white hover:!bg-gray-50"
              dropdownClass="!z-[100] !rounded-[6px] !shadow-lg !border !border-gray-200"
              enableSearch
              disableSearchIcon
              searchPlaceholder={t('register.phone.search')}
              searchNotFound={t('register.phone.search.none')}
            />
          </div>
        </FormRow>
      </div>
      <div className="border-t border-gray-100 mt-3 pt-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          {t('register.personal.address')}
        </p>
        <AddressFields
          value={personalAddress}
          onChange={setPersonalAddress}
          variant="formRow"
          requiredFields
        />
      </div>
    </div>
  );

  const inviteFields =
    role === 'employee' || role === 'manager' ? (
      <div className="border-t border-gray-100 mt-3 pt-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
          {t('register.invite.section')}
        </p>
        <p className="text-sm text-gray-600 mb-3">{t('register.invite.question')}</p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {(
            [
              { value: true, label: t('register.invite.yes') },
              { value: false, label: t('register.invite.no') },
            ] as const
          ).map((opt) => (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => {
                setHasInviteCode(opt.value);
                setError(null);
                if (!opt.value) {
                  setInviteInput('');
                  setInviteInfo(null);
                  setInviteLookupError(null);
                  setInviteFromLink(false);
                } else if (inviteFromUrl && !inviteInput.trim()) {
                  setInviteInput(inviteFromUrl);
                  setInviteFromLink(true);
                }
              }}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition ${
                hasInviteCode === opt.value
                  ? 'border-[#0065F4] bg-[#F1F5FF] text-[#0057D3]'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {hasInviteCode && (
          <div>
            <FormRow layout="stacked" icon={LORDICONS.lock} label={t('register.invite.code')} required>
              <input
                type="text"
                id="inviteCode"
                value={inviteInput}
                onChange={(e) => {
                  setInviteInput(e.target.value.toUpperCase());
                  setInviteFromLink(false);
                  setInviteInfo(null);
                  setInviteLookupError(null);
                  setError(null);
                }}
                className={`${fieldCls} font-mono tracking-wide`}
                placeholder={t('register.invite.placeholder')}
                autoComplete="off"
                spellCheck={false}
              />
            </FormRow>
            {inviteFromLink && inviteFromUrl && inviteInput.trim() === inviteFromUrl && (
              <p className="text-[11px] text-primary-600 mt-1">{t('register.invite.from.link')}</p>
            )}
            {!inviteLookupLoading && !inviteLookupError && !inviteInfo && (
              <p className="text-[11px] text-gray-400 mt-1">{t('register.invite.hint')}</p>
            )}
            {inviteLookupLoading && (
              <p className="text-[11px] text-gray-500 mt-1">{t('register.invite.checking')}</p>
            )}
            {inviteLookupError && (
              <p className="text-[11px] text-red-600 mt-1">{inviteLookupError}</p>
            )}
            {inviteInfo?.businessName && !inviteLookupError && (
              <div className="mt-2 flex items-center gap-2 rounded-xl bg-primary-50 border border-primary-100 px-3 py-2">
                <LordIcon src={LORDICONS.building} size={18} color="#0065F4" trigger="hover" />
                <p className="text-xs text-primary-800">
                  {t('register.invite.joining').replace('{name}', inviteInfo.businessName)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    ) : null;

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* Left - registration steps */}
      <div className="w-full lg:w-1/2 h-screen flex flex-col overflow-hidden">
        <div className="shrink-0 px-6 sm:px-10 xl:px-16 py-5">
          <div className="w-full max-w-xl mx-auto flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center hover:opacity-80 transition-opacity"
              aria-label="Ompli"
            >
              <img src="/images/Ompli-logo-blue.png" alt="Ompli" className="h-10 w-auto" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm font-medium text-gray-500 hover:text-primary-600 transition-colors"
            >
              {t('register.sign.in')}
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <div className="min-h-full flex items-center justify-center px-6 sm:px-10 xl:px-16 py-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full max-w-xl"
          >
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight">
                {t('register.join.ompli')}
              </h1>
            </div>

            <Stepper />

            {error && (
              <div className="text-red-600 mb-4 text-sm rounded-xl bg-red-50 border border-red-100 px-3 py-2">
                {error}
              </div>
            )}

            <div>
        {step === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="space-y-3">
              {(
                [
                  { key: 'employer' as const, icon: LORDICONS.building, label: t('register.im.employer') },
                  { key: 'employee' as const, icon: LORDICONS.user, label: t('register.im.employee') },
                  { key: 'manager' as const, icon: LORDICONS.users, label: t('register.im.manager') },
                ] as const
              ).map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRole(r.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 border rounded-2xl text-sm font-semibold transition ${
                    role === r.key
                      ? 'border-[#0065F4] bg-[#F1F5FF] text-[#0057D3] shadow-sm'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <LordIcon
                    src={r.icon}
                    trigger="hover"
                    size={18}
                    color={role === r.key ? '#0057D3' : '#6B7280'}
                    hoverColor="#0065F4"
                    target="button"
                  />
                  <span>{r.label}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-8">
              <button
                type="button"
                onClick={handleNext}
                className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition"
              >
                {t('register.next')}
              </button>
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.form initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {oauthProvider ? (
              <div className="bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 mb-3 flex items-center gap-3">
                <CalendarBrandIcon provider={oauthProvider} size={22} />
                <div>
                  <div className="text-sm font-semibold text-primary-900">{t('register.oauth.linked')}</div>
                  <div className="text-xs text-primary-600">{t('register.oauth.calendar.hint')}</div>
                </div>
              </div>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
              <FormRow layout="stacked" icon={LORDICONS.user} label={t('register.first.name')} required>
                <input
                  type="text"
                  id="firstName"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className={fieldCls}
                />
              </FormRow>
              <FormRow layout="stacked" icon={LORDICONS.user} label={t('register.last.name')} required>
                <input
                  type="text"
                  id="lastName"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className={fieldCls}
                />
              </FormRow>
            </div>
            <FormRow layout="stacked" icon={LORDICONS.person} label={t('register.username')} required>
              <input
                type="text"
                id="username"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={fieldCls}
              />
            </FormRow>
            <FormRow layout="stacked" icon={LORDICONS.email} label={t('register.email')} required>
              <input
                type="email"
                id="email"
                required
                readOnly={emailLocked}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={fieldCls}
              />
            </FormRow>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
              <FormRow layout="stacked" icon={LORDICONS.lock} label={t('register.password')} required>
                <input
                  type="password"
                  id="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={fieldCls}
                />
              </FormRow>
              <FormRow layout="stacked" icon={LORDICONS.lock} label={t('register.confirm.password')} required>
                <input
                  type="password"
                  id="confirmPassword"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={fieldCls}
                />
              </FormRow>
            </div>
            <div className="flex justify-between mt-8">
              <button type="button" onClick={handleBack} className="text-[#0065F4] font-medium">
                {t('register.back')}
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition"
              >
                {t('register.next')}
              </button>
            </div>
          </motion.form>
        )}

        {step === 2 && (
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="overflow-y-auto pr-1"
          >
            {personalFields}
            {inviteFields}

            {role === 'employer' && (
              <div className="border-t border-gray-100 mt-3 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {t('register.section.company')}
                </p>

                <div className="flex items-center gap-4 py-2">
                  <div className="relative flex-shrink-0">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt=""
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-100 bg-white"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#EAF1FF] flex items-center justify-center border-2 border-gray-100">
                        <LordIcon src={LORDICONS.building} size={28} color="#0065F4" trigger="hover" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg cursor-pointer transition-colors">
                      <LordIcon src={LORDICONS.upload} trigger="hover" size={16} color="#6B7280" target="label" />
                      {t('settings.company.logo.upload')}
                      <input type="file" accept="image/*" className="hidden" onChange={onLogoPick} />
                    </label>
                    <p className="text-xs text-gray-400 mt-1">{t('settings.company.logo.hint')}</p>
                  </div>
                </div>

                <FormRow layout="stacked" icon={LORDICONS.briefcase} label={t('settings.business.name')} required>
                  <input
                    type="text"
                    id="businessName"
                    required
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    className={fieldCls}
                    placeholder={t('settings.business.name.placeholder')}
                  />
                </FormRow>

                <FormRow
                  layout="stacked"
                  icon={LORDICONS.search}
                  label={t('settings.business.companyName')}
                  align="start"
                  required
                >
                  <div className="w-full">
                    <AresSuggestInput
                      id="companyName"
                      label={t('settings.business.companyName')}
                      value={formData.companyName}
                      onChange={(v) => setFormData({ ...formData, companyName: v })}
                      onSelect={applyAresSuggestion}
                      placeholder={t('settings.business.companyName.placeholder')}
                      hideLabel
                      inputClassName={fieldCls}
                    />
                    <p className="text-[11px] text-gray-400 mt-1">{t('settings.business.ares.hint')}</p>
                  </div>
                </FormRow>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                  <FormRow layout="stacked" icon={LORDICONS.document} label={t('settings.business.ico')} required>
                    <input
                      type="text"
                      id="ico"
                      required
                      value={formData.ico}
                      onChange={(e) => setFormData({ ...formData, ico: e.target.value })}
                      className={fieldCls}
                    />
                  </FormRow>
                  <FormRow layout="stacked" icon={LORDICONS.clipboard} label={t('settings.business.dic')}>
                    <input
                      type="text"
                      id="dic"
                      value={formData.dic}
                      onChange={(e) => setFormData({ ...formData, dic: e.target.value })}
                      className={fieldCls}
                    />
                  </FormRow>
                </div>

                <div className="border-t border-gray-100 mt-3 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {t('settings.business.section.address')}
                  </p>
                  <AddressFields
                    value={businessAddress}
                    onChange={setBusinessAddress}
                    enablePhoton
                    variant="formRow"
                    requiredFields
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                  <FormRow layout="stacked" icon={LORDICONS.email} label={t('settings.business.email')}>
                    <input
                      type="email"
                      id="businessEmail"
                      value={formData.businessEmail}
                      onChange={(e) => setFormData({ ...formData, businessEmail: e.target.value })}
                      className={fieldCls}
                    />
                  </FormRow>
                  <FormRow layout="stacked" icon={LORDICONS.phone} label={t('settings.business.phone')}>
                    <input
                      type="text"
                      id="businessPhone"
                      value={formData.businessPhone}
                      onChange={(e) => setFormData({ ...formData, businessPhone: e.target.value })}
                      className={fieldCls}
                    />
                  </FormRow>
                </div>
                <FormRow layout="stacked" icon={LORDICONS.home} label={t('settings.business.website')}>
                  <input
                    type="url"
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className={fieldCls}
                    placeholder="https://"
                  />
                </FormRow>
                <div className="border-t border-gray-100 mt-3 pt-4">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-600 mb-2"
                  >
                    {t('settings.business.description')}
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-2.5 py-2 border border-gray-200 rounded-[6px] text-[13px] text-black bg-white resize-none focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('settings.business.description.placeholder')}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between mt-6 sticky bottom-0 bg-white pt-3">
              <button type="button" onClick={handleBack} className="text-[#0065F4] font-medium">
                {t('register.back')}
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition"
              >
                {t('register.next')}
              </button>
            </div>
          </motion.form>
        )}

        {step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm max-h-[50vh] overflow-y-auto">
              {(
                [
                  {
                    icon: LORDICONS.briefcase,
                    label: t('register.review.role'),
                    value:
                      role === 'employer'
                        ? t('register.im.employer')
                        : role === 'employee'
                          ? t('register.im.employee')
                          : t('register.im.manager'),
                  },
                  {
                    icon: LORDICONS.user,
                    label: t('register.review.name'),
                    value: `${formData.firstName} ${formData.lastName}`.trim(),
                  },
                  { icon: LORDICONS.person, label: t('register.username'), value: formData.username },
                  { icon: LORDICONS.email, label: t('register.email'), value: formData.email },
                  {
                    icon: LORDICONS.users,
                    label: t('register.sex'),
                    value:
                      formData.sex === 'male'
                        ? t('register.sex.male')
                        : formData.sex === 'female'
                          ? t('register.sex.female')
                          : formData.sex === 'other'
                            ? t('register.sex.other')
                            : formData.sex,
                  },
                  { icon: LORDICONS.phone, label: t('register.phone'), value: formData.phone },
                  {
                    icon: LORDICONS.pin,
                    label: t('register.personal.address'),
                    value: formatAddressLabel(personalAddress),
                  },
                  ...((role === 'employee' || role === 'manager') && hasInviteCode && inviteInput.trim()
                    ? [
                        {
                          icon: LORDICONS.lock,
                          label: t('register.invite.code'),
                          value: inviteInfo?.businessName
                            ? `${inviteInput.trim()} · ${inviteInfo.businessName}`
                            : inviteInput.trim(),
                        },
                      ]
                    : []),
                  ...(avatarFile
                    ? [
                        {
                          icon: LORDICONS.upload,
                          label: t('profile.upload.photo'),
                          value: t('register.review.selected'),
                        },
                      ]
                    : []),
                  ...(role === 'employer'
                    ? [
                        {
                          icon: LORDICONS.briefcase,
                          label: t('settings.business.name'),
                          value: formData.businessName,
                          divider: true,
                        },
                        {
                          icon: LORDICONS.search,
                          label: t('settings.business.companyName'),
                          value: formData.companyName,
                        },
                        {
                          icon: LORDICONS.document,
                          label: t('settings.business.ico'),
                          value: formData.ico,
                        },
                        ...(formData.dic
                          ? [
                              {
                                icon: LORDICONS.clipboard,
                                label: t('settings.business.dic'),
                                value: formData.dic,
                              },
                            ]
                          : []),
                        {
                          icon: LORDICONS.location,
                          label: t('settings.business.section.address'),
                          value: formatAddressLabel(businessAddress),
                        },
                        ...(logoFile
                          ? [
                              {
                                icon: LORDICONS.upload,
                                label: t('settings.company.logo.upload'),
                                value: t('register.review.selected'),
                              },
                            ]
                          : []),
                      ]
                    : []),
                ] as Array<{
                  icon: string;
                  label: string;
                  value: string;
                  divider?: boolean;
                }>
              ).map((row) => (
                <div
                  key={row.label}
                  className={`flex items-start gap-2.5 py-2 ${
                    row.divider ? 'border-t border-gray-200 mt-2 pt-3' : ''
                  }`}
                >
                  <LordIcon
                    src={row.icon}
                    size={18}
                    color="#4B5563"
                    trigger="hover"
                    className="shrink-0 mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-500">{row.label}</div>
                    <div className="text-sm text-gray-900 break-words">{row.value}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-8">
              <button type="button" onClick={handleBack} className="text-[#0065F4] font-medium">
                {t('register.back')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition"
                disabled={loading}
              >
                {loading ? t('register.registering') : t('register.create.account')}
              </button>
            </div>
          </motion.div>
        )}

            </div>

            <div className="mt-8 pt-4 border-t border-gray-100 text-sm text-gray-500">
              {t('register.already.have.account')}{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="font-medium text-[#0065F4] hover:text-[#3384F6]"
              >
                {t('register.sign.in')}
              </button>
            </div>
          </motion.div>
          </div>
        </div>
      </div>

      {/* Right - info panel + receptionist (same content width as left form) */}
      <div className="hidden lg:flex w-1/2 relative h-screen overflow-hidden bg-gradient-to-br from-[#0065F4] via-[#0057D3] to-[#0B3FA8]">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35), transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.18), transparent 40%)',
          }}
        />
        <div className="relative z-10 flex flex-col justify-center items-center w-full h-full px-6 sm:px-10 xl:px-16 py-14">
          <div className="w-full max-w-2xl h-[90%] max-h-[900px] min-h-0 flex flex-col">
            <div className="relative rounded-[28px] bg-white/20 backdrop-blur-md border border-white/35 shadow-2xl flex-1 flex flex-col overflow-hidden min-h-0">
              <img
                src="/images/receptionist-background.png"
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover object-[center_62%] opacity-45 scale-[1.35] pointer-events-none select-none"
              />
              <div className="relative z-10 flex-1 flex items-end justify-center px-6 pt-8 min-h-0">
                <motion.img
                  key={step}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45 }}
                  src="/images/receptionist.png"
                  alt=""
                  className="w-full max-w-xl max-h-full object-contain drop-shadow-2xl select-none pointer-events-none scale-110 origin-bottom"
                />
              </div>
              <div className="relative z-10 px-8 pb-8 pt-4">
                <motion.h2
                  key={`t-${step}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-2xl xl:text-3xl font-semibold tracking-tight !text-white"
                >
                  {panelCopy.title}
                </motion.h2>
                <motion.p
                  key={`d-${step}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                  className="mt-2 text-sm xl:text-base text-white/90 leading-relaxed"
                >
                  {panelCopy.desc}
                </motion.p>
                <div className="mt-6 flex items-center gap-2">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === step ? 'w-8 bg-white' : 'w-1.5 bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <p className="mt-6 text-center text-xs text-white/50">{t('register.panel.footer')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
